/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **One *Test as* choice per surface** (`#979`), read by everything on it that runs the rig: the
 * test, the resolved-request preview and the rows' resolution marks, and the response preview.
 *
 * The choice lived inside `QuickTestPanel`, so a test could run as a phone while every row mark
 * beside it resolved as the server's default device, and the response preview as that default too.
 * Three consumers of one harness on one page, and only one of them could be told what it was. A
 * source previewed as one device and tested as another has been checked twice and validated once
 * (`#927 · D1`).
 *
 * The selection starts **empty**, which means *the rig's defaults*. The picker shows those defaults
 * from the catalog rather than writing them into the selection, so loading the catalog changes
 * nothing that was already resolved, and the console never restates a default the server owns.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { demandSourceService, type TestContextCatalog, type TestRunContext } from '../services/demand-source.service';

export interface TestContextState {
  /** The choices, or null while loading or when the catalog could not be read. */
  catalog: TestContextCatalog | null;
  /** What the operator chose. A missing part means the rig's default for it. */
  selection: TestRunContext;
  setSelection: (next: TestRunContext) => void;
}

export function useTestContext(): TestContextState {
  // The catalog changes with a release or when the content library is edited, never per request:
  // held for the session rather than re-read (slow-changing data is held in memory).
  const { data } = useQuery({
    queryKey: ['test-contexts'],
    queryFn: () => demandSourceService.getTestContextCatalog(),
    staleTime: Infinity,
  });
  const [selection, setSelectionState] = React.useState<TestRunContext>({});
  const setSelection = React.useCallback((next: TestRunContext) => {
    // Drop empty parts, so an unchosen part stays *the rig's default* rather than an empty id.
    setSelectionState({
      ...(next.deviceType ? { deviceType: next.deviceType } : {}),
      ...(next.contentId ? { contentId: next.contentId } : {}),
    });
  }, []);
  return { catalog: data ?? null, selection, setSelection };
}
